/**
 * Shared Pricing Cards Components
 *
 * DRY components used by both the Settings Plans tab and the standalone /pricing page.
 * Handles plan fetching, billing toggle, card rendering, and §312j BGB legal consent.
 *
 * @see sl-pk28.4, sl-jqld.2, sl-ycf7.6.3, sl-53cw.1.2
 */

import { notify } from '@affine/component';
import { formatPrice } from '@affine/core/utils/format-price';
import {
  getStartingPrice,
  parseSeatTiers,
} from '@affine/core/utils/graduated-pricing';
import {
  createCheckoutSessionMutation,
  type PricingPlansQuery,
  pricingPlansQuery,
  SubscriptionRecurring,
  switchPlanIntervalMutation,
  trialEligibleQuery,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { DoneIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AuthService, GraphQLService } from '../../modules/cloud';
import { CONTACT_EMAIL } from '../../modules/streamlain/contact';
import {
  buildSubscriptionOrderSummary,
  PurchaseConfirmDialog,
} from '../consent';
import * as styles from './pricing-cards.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PricingPlan = PricingPlansQuery['pricingPlans'][number];

type I18nFn = ReturnType<typeof useI18n>;

export interface PricingCardsAutoCheckout {
  coupon?: string | null;
  plan: string;
  recurring: SubscriptionRecurring;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isProviderlessCta(ctaType: PricingPlan['ctaType']): boolean {
  return ctaType === 'contact' || ctaType === 'demo';
}

function recurringToBillingInterval(
  recurring: SubscriptionRecurring
): 'monthly' | 'yearly' {
  return recurring === SubscriptionRecurring.Yearly ? 'yearly' : 'monthly';
}

function getCtaLabel(
  t: I18nFn,
  ctaType: PricingPlan['ctaType'],
  isCurrent: boolean,
  isLoading: boolean,
  isFree: boolean,
  isTrialEligible?: boolean
): string {
  if (isCurrent) return t['com.streamlain.payment.pricing.current-plan']();
  if (isLoading) return t['com.streamlain.payment.pricing.loading']();
  if (isFree) return t['com.streamlain.payment.pricing.free-start']();
  // sl-r9ix.3.2: Show trial CTA for eligible users
  if (isTrialEligible)
    return t['com.streamlain.payment.pricing.start-free-trial']();
  switch (ctaType) {
    case 'demo':
      return t['com.streamlain.payment.pricing.request-demo']();
    case 'contact':
      return t['com.streamlain.payment.pricing.contact-us']();
    default:
      return t['com.streamlain.payment.pricing.upgrade']();
  }
}

// ---------------------------------------------------------------------------
// Dynamic Feature Generator — builds feature list from plan quotas
// ---------------------------------------------------------------------------

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) {
    const gb = bytes / (1024 * 1024 * 1024);
    return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
  }
  const mb = bytes / (1024 * 1024);
  return `${Number.isInteger(mb) ? mb : mb.toFixed(0)} MB`;
}

/**
 * Generate feature strings dynamically from plan quotas.
 * Static features from `plan.features` are appended after quota-based ones.
 * Enterprise/contact plans use "nach Absprache" for unlimited/custom values.
 */
function buildPlanFeatures(plan: PricingPlan, locale: 'de' | 'en'): string[] {
  const q = plan.quotas;
  const providerlessCta = isProviderlessCta(plan.ctaType);
  const de = locale === 'de';

  // Split static features: "Alles aus X" goes first, rest goes after quotas
  const headFeatures: string[] = [];
  const tailFeatures: string[] = [];
  for (const f of plan.features) {
    if (f.startsWith('Alles aus') || f.startsWith('Everything in')) {
      headFeatures.push(f);
    } else {
      tailFeatures.push(f);
    }
  }

  const features: string[] = [...headFeatures];

  // Providerless CTA plans: single summary instead of 5× "nach Absprache"
  if (providerlessCta) {
    features.push(
      de
        ? 'Mitglieder, Storage, KI- & Automation-Credits nach Absprache'
        : 'Members, storage, AI & automation credits by arrangement'
    );
  } else {
    // 1. Members
    const memberLabel =
      q.memberLimit === 1
        ? de
          ? 'Mitglied'
          : 'Member'
        : de
          ? 'Mitglieder'
          : 'Members';
    features.push(
      q.memberLimit === -1
        ? de
          ? 'Unbegrenzte Mitglieder'
          : 'Unlimited members'
        : `${q.memberLimit} ${memberLabel}`
    );

    // 2. Guests
    const guestLabel =
      q.guestLimit === 1 ? (de ? 'Gast' : 'Guest') : de ? 'Gäste' : 'Guests';
    features.push(
      q.guestLimit === -1
        ? de
          ? 'Unbegrenzte Gäste'
          : 'Unlimited guests'
        : `${q.guestLimit} ${guestLabel}`
    );

    // 3. Storage
    const storageBytes = Number(q.storage);
    const storagePerMemberBytes = Number(q.storagePerMember);
    features.push(
      storageBytes === -1
        ? de
          ? 'Unbegrenzter Speicher'
          : 'Unlimited storage'
        : `${formatBytes(storageBytes)} ${de ? 'Speicher' : 'Storage'}`
    );
    if (storagePerMemberBytes > 0) {
      features.push(
        de
          ? `+ ${formatBytes(storagePerMemberBytes)} Speicher je Mitglied`
          : `+ ${formatBytes(storagePerMemberBytes)} storage per member`
      );
    }

    // 4. Max file size (blob limit)
    const blobBytes = Number(q.blobLimit);
    if (blobBytes > 0) {
      features.push(
        `${formatBytes(blobBytes)} max. ${de ? 'Dateigröße' : 'file size'}`
      );
    }

    // 5. Credits
    if (q.monthlyCreditGrant != null && q.monthlyCreditGrant > 0) {
      features.push(
        de
          ? `${q.monthlyCreditGrant} Credits für KI & Automations / Monat`
          : `${q.monthlyCreditGrant} credits for AI & automations / month`
      );
    }

    // 6. History
    features.push(
      q.historyDays === -1
        ? de
          ? 'Unbegrenzte History'
          : 'Unlimited history'
        : `${q.historyDays} ${de ? 'Tage History' : 'days history'}`
    );
  }

  // 7. Remaining static features (Automations, SSO, Support, Rollen & Rechte, etc.)
  features.push(...tailFeatures);

  // 8. Extra seats (only if available)
  if (!providerlessCta && q.maxExtraSeats !== 0) {
    features.push(de ? 'Extra-Seats zubuchbar' : 'Extra seats available');
  }

  return features;
}

// ---------------------------------------------------------------------------
// PlanCard
// ---------------------------------------------------------------------------

export function PlanCard({
  plan,
  billingInterval,
  onSubscribe,
  isLoading,
  isCurrent,
  isOverride,
  currentRecurring,
  onSwitchInterval,
  isSwitching,
  isCurrentActionable,
  isTrialEligible,
  onStartTrial,
}: {
  plan: PricingPlan;
  billingInterval: 'monthly' | 'yearly';
  onSubscribe: (priceId: string) => void;
  isLoading: boolean;
  isCurrent: boolean;
  isOverride: boolean;
  /** Current subscription billing interval ('Monthly' | 'Yearly'), only set when isCurrent */
  currentRecurring?: string | null;
  /** Callback to switch the billing interval for the current plan */
  onSwitchInterval?: (newRecurring: 'Monthly' | 'Yearly') => void;
  /** Whether an interval switch is in progress */
  isSwitching?: boolean;
  /** Show the current badge but keep the CTA actionable (used for trialing subscriptions). */
  isCurrentActionable?: boolean;
  /** sl-r9ix.3.2: User is eligible for a free Pro trial */
  isTrialEligible?: boolean;
  /** sl-r9ix.3.2: Start free trial callback */
  onStartTrial?: () => void;
}) {
  const t = useI18n();

  const locale = t.language?.startsWith('en') ? 'en-US' : 'de-DE';

  const monthlyPrice = plan.prices.find(
    (p: PricingPlan['prices'][number]) => p.interval === 'monthly'
  );
  const yearlyPrice = plan.prices.find(
    (p: PricingPlan['prices'][number]) => p.interval === 'yearly'
  );
  const activePrice =
    billingInterval === 'yearly' ? (yearlyPrice ?? monthlyPrice) : monthlyPrice;

  // sl-xaz5.6: Use explicit isFreePlan flag instead of prices.length heuristic
  const isFree = plan.isFreePlan === true;
  const hasPrice = activePrice && activePrice.amount > 0;

  const monthlyEquivalent =
    billingInterval === 'yearly' && yearlyPrice
      ? Math.round(yearlyPrice.amount / 12)
      : (activePrice?.amount ?? 0);

  // Interval mismatch: user IS on this plan, but toggle shows a different interval.
  // Example: user has Monthly Pro, but toggle is set to Yearly → show "Switch to yearly".
  const isIntervalMismatch =
    isCurrent &&
    !isFree &&
    !!currentRecurring &&
    ((billingInterval === 'monthly' && currentRecurring !== 'Monthly') ||
      (billingInterval === 'yearly' && currentRecurring !== 'Yearly'));

  // When toggle matches the subscription interval, this card IS the current plan.
  // When toggle differs, this card shows the switch-interval CTA instead.
  const showAsCurrent = isCurrent && !isIntervalMismatch;
  const ctaIsCurrent = showAsCurrent && !isCurrentActionable;
  const statusBadges: Array<{
    key: 'override' | 'current' | 'popular';
    className: string;
    testId?: string;
    label: string;
  }> = [];
  if (isOverride) {
    statusBadges.push({
      key: 'override',
      className: styles.pricingOverrideBadge,
      testId: 'pricing-override-badge',
      label: t['com.streamlain.payment.pricing.override-plan'](),
    });
  }
  if (showAsCurrent) {
    statusBadges.push({
      key: 'current',
      className: styles.pricingCurrentBadge,
      testId: 'pricing-current-badge',
      label: t['com.streamlain.payment.pricing.current-plan'](),
    });
  }
  if (statusBadges.length === 0 && plan.badge) {
    statusBadges.push({
      key: 'popular',
      className: styles.pricingBadge,
      label: plan.badge,
    });
  }

  const handleClick = useCallback(() => {
    // Interval mismatch → trigger interval switch (not a new checkout)
    if (isIntervalMismatch && onSwitchInterval) {
      onSwitchInterval(currentRecurring === 'Monthly' ? 'Yearly' : 'Monthly');
      return;
    }
    if (isProviderlessCta(plan.ctaType)) {
      window.open(`mailto:${CONTACT_EMAIL}`, '_blank');
      return;
    }
    // sl-r9ix.3.2: Trial-eligible users skip consent dialog
    if (isTrialEligible && onStartTrial) {
      onStartTrial();
      return;
    }
    if (activePrice) {
      onSubscribe(activePrice.priceId);
    }
  }, [
    isIntervalMismatch,
    onSwitchInterval,
    currentRecurring,
    plan.ctaType,
    activePrice,
    onSubscribe,
    isTrialEligible,
    onStartTrial,
  ]);

  return (
    <div
      className={styles.pricingCard}
      data-featured={plan.featured}
      data-current={showAsCurrent}
      data-plan={plan.productName}
      data-pricing-plan-name={plan.productName}
    >
      {statusBadges.length > 0 && (
        <div className={styles.pricingBadgeRail}>
          {statusBadges.map(badge => (
            <div
              key={badge.key}
              data-testid={badge.testId}
              className={badge.className}
            >
              <span className={styles.pricingBadgeText}>{badge.label}</span>
            </div>
          ))}
        </div>
      )}

      <div className={styles.pricingCardHeader}>
        <div className={styles.pricingCardName}>{plan.productName}</div>
        {plan.description && (
          <div className={styles.pricingCardDescription}>
            {plan.description}
          </div>
        )}
      </div>

      <div
        data-testid="pricing-card-price-section"
        className={styles.pricingCardPriceSection}
      >
        {isFree ? (
          <div className={styles.pricingCardPrice}>
            {t['com.streamlain.payment.pricing.free']()}
          </div>
        ) : hasPrice ? (
          <>
            <div className={styles.pricingCardPrice}>
              {formatPrice(monthlyEquivalent, activePrice.currency, locale)}
              <span className={styles.pricingCardPriceInterval}>
                {t['com.streamlain.payment.pricing.per-month']()}
              </span>
            </div>
            {billingInterval === 'yearly' && yearlyPrice && monthlyPrice && (
              <div className={styles.pricingCardPriceAnnual}>
                {formatPrice(yearlyPrice.amount, yearlyPrice.currency, locale)}{' '}
                {t['com.streamlain.payment.pricing.billed-yearly']()}
                {(() => {
                  const discount = Math.round(
                    (1 - yearlyPrice.amount / (monthlyPrice.amount * 12)) * 100
                  );
                  return discount > 0 ? (
                    <span className={styles.pricingCardDiscount}>
                      {' '}
                      &ndash;{discount}%
                    </span>
                  ) : null;
                })()}
              </div>
            )}
            {(() => {
              const tiers = parseSeatTiers(plan.quotas.seatTiers);
              const startingPrice = tiers?.monthly
                ? getStartingPrice(tiers.monthly)
                : 0;
              if (startingPrice <= 0) return null;
              return (
                <div className={styles.pricingCardSeatPrice}>
                  {t['com.streamlain.payment.pricing.seat-price']({
                    price: formatPrice(
                      startingPrice,
                      activePrice?.currency ?? 'eur',
                      locale
                    ),
                  })}
                </div>
              );
            })()}
          </>
        ) : (
          <div className={styles.pricingCardPrice}>
            {t['com.streamlain.payment.pricing.on-request']()}
          </div>
        )}
      </div>

      <button
        className={styles.pricingCardCta}
        data-testid={`pricing-cta-${plan.productName}`}
        data-featured={plan.featured}
        data-current={ctaIsCurrent}
        onClick={handleClick}
        disabled={isLoading || isSwitching || ctaIsCurrent}
      >
        {ctaIsCurrent && (
          <span className={styles.pricingCardCtaCheck}>&#10003;</span>
        )}
        {isIntervalMismatch
          ? currentRecurring === 'Monthly'
            ? t['com.streamlain.payment.pricing.switch-to-yearly']()
            : t['com.streamlain.payment.pricing.switch-to-monthly']()
          : getCtaLabel(
              t,
              plan.ctaType,
              ctaIsCurrent,
              isLoading,
              isFree,
              isTrialEligible
            )}
      </button>

      {(() => {
        const featureLang = locale === 'en-US' ? 'en' : 'de';
        const dynamicFeatures = buildPlanFeatures(plan, featureLang);
        return dynamicFeatures.length > 0 ? (
          <ul className={styles.pricingCardFeatures}>
            {dynamicFeatures.map((feature: string, i: number) => (
              <li key={i} className={styles.pricingCardFeatureItem}>
                <DoneIcon className={styles.pricingCardFeatureIcon} />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        ) : null;
      })()}
    </div>
  );
}

// ---------------------------------------------------------------------------
// BillingToggle
// ---------------------------------------------------------------------------

export function BillingToggle({
  interval,
  onChange,
}: {
  interval: 'monthly' | 'yearly';
  onChange?: (v: 'monthly' | 'yearly') => void;
}) {
  const t = useI18n();
  const disabled = !onChange;
  return (
    <div className={styles.billingToggle}>
      <button
        className={styles.billingToggleButton}
        data-active={interval === 'monthly'}
        disabled={disabled}
        onClick={() => onChange?.('monthly')}
      >
        {t['com.streamlain.payment.pricing.monthly']()}
      </button>
      <button
        className={styles.billingToggleButton}
        data-active={interval === 'yearly'}
        disabled={disabled}
        onClick={() => onChange?.('yearly')}
      >
        {t['com.streamlain.payment.pricing.yearly']()}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PricingCards — Full orchestrator (data fetching + rendering)
// ---------------------------------------------------------------------------

export interface PricingCardsProps {
  /**
   * Override the default payment-provider checkout behavior.
   * Called when a user clicks subscribe on a plan.
   * If not provided, defaults to provider checkout session creation.
   */
  onSubscribe?: (priceId: string) => void;
  /**
   * Effective feature-access plan (e.g. Team through an admin override).
   * Used for access-sensitive CTA behavior, not necessarily the paid plan.
   */
  activePlanName?: string | null;
  /** Real current plan shown as the "Current" badge. */
  currentPlanName?: string | null;
  /** Admin override access plan shown as "Override" when the plan has a card. */
  overridePlanName?: string | null;
  /** Real paid subscription plan used for Stripe mutations. */
  billingPlanName?: string | null;
  /**
   * sl-dl66.3.1: Current billing interval from the active subscription.
   * Used to show the interval switch button on the current plan card.
   */
  currentRecurring?: string | null;
  /**
   * sl-3uon: Current subscription period end date (ISO string).
   * Used to show the effective date in the deferred downgrade confirmation dialog.
   */
  subscriptionEnd?: string | null;
  /**
   * sl-fwcn.5.2: §312j BGB — active addon costs for total cost display in consent dialog.
   * Addons cascade interval with the plan, so users must see the total.
   */
  activeAddonCosts?: Array<{ label: string; amountCents: number }>;
  /**
   * sl-gegg.2.1: True when subscription status is Trialing.
   * Trial users see "Upgrade now" instead of "Current plan" on their trial plan card.
   */
  isTrialing?: boolean;
  /**
   * Deep-link request from /subscribe after streamlain.de plan selection.
   * Opens the Settings Pricing Plans tab, scrolls the chosen card into view,
   * then opens the same confirmation dialog as a manual click.
   */
  autoCheckout?: PricingCardsAutoCheckout | null;
  /** Optional coupon carried from external pricing links. */
  checkoutCoupon?: string | null;
  /** Called once the auto checkout dialog was opened so URL params can be cleared. */
  onAutoCheckoutHandled?: () => void;
}

export function PricingCards({
  onSubscribe: onSubscribeOverride,
  activePlanName,
  currentPlanName,
  overridePlanName,
  billingPlanName,
  currentRecurring,
  subscriptionEnd,
  activeAddonCosts,
  isTrialing = false,
  autoCheckout,
  checkoutCoupon,
  onAutoCheckoutHandled,
}: PricingCardsProps) {
  const t = useI18n();
  const graphqlService = useService(GraphQLService);
  const authService = useService(AuthService);
  const status = useLiveData(authService.session.status$);
  const loggedIn = status === 'authenticated';

  const [plans, setPlans] = useState<PricingPlan[] | null>(null);
  // Initialize billing interval from current subscription.
  // sl-3uon: No longer locks to yearly — toggle is always free to switch (display only).
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>(
    currentRecurring === 'Yearly' ? 'yearly' : 'monthly'
  );
  const autoCheckoutHandledKeyRef = useRef<string | null>(null);

  // Sync toggle when subscription data loads async (currentRecurring may be
  // null on first render, then arrives from GraphQL).
  useEffect(() => {
    if (autoCheckout) {
      return;
    }
    if (currentRecurring === 'Yearly') {
      setBillingInterval('yearly');
    } else if (currentRecurring === 'Monthly') {
      setBillingInterval('monthly');
    }
  }, [autoCheckout, currentRecurring]);

  useEffect(() => {
    if (!autoCheckout) {
      return;
    }
    setBillingInterval(recurringToBillingInterval(autoCheckout.recurring));
  }, [autoCheckout]);
  const [loadingPriceId, setLoadingPriceId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // §312j/§356 BGB: Confirmation dialog state
  const [pendingPriceId, setPendingPriceId] = useState<string | null>(null);
  const [pendingPlanName, setPendingPlanName] = useState<string | null>(null);

  // sl-r9ix.3.2: Trial eligibility (only fetched for logged-in users on Free plan)
  const [trialEligible, setTrialEligible] = useState(false);

  const lang = t.language?.startsWith('en') ? 'en' : 'de';

  // Fetch available plans
  useEffect(() => {
    graphqlService
      .gql({
        query: pricingPlansQuery,
        variables: { language: lang },
      })
      .then((res: PricingPlansQuery) => setPlans(res.pricingPlans))
      .catch((err: Error) => setError(err.message));
  }, [graphqlService, lang]);

  // sl-r9ix.3.2: Fetch trial eligibility (skip if user has a paid plan)
  useEffect(() => {
    const hasPaidPlan = activePlanName && activePlanName !== 'Free';
    if (!loggedIn || hasPaidPlan) {
      setTrialEligible(false);
      return;
    }
    graphqlService
      .gql({ query: trialEligibleQuery })
      .then((res: { trialEligible: boolean }) =>
        setTrialEligible(res.trialEligible)
      )
      .catch(() => setTrialEligible(false));
  }, [graphqlService, loggedIn, activePlanName]);

  // sl-xaz5.6: Defense-in-depth — filter incomplete plans (backend already filters,
  // but frontend ensures no broken cards are shown)
  const completePlans = useMemo(
    () =>
      (plans ?? []).filter(
        p => p.isFreePlan || p.prices.length > 0 || isProviderlessCta(p.ctaType)
      ),
    [plans]
  );

  const activeProductNames = useMemo(() => {
    const names = new Set<string>();
    // Tenant subscription is the single source of truth
    if (activePlanName) {
      names.add(activePlanName);
    }
    // If logged in with no active subscription, user is on the Free plan
    if (loggedIn && names.size === 0) {
      const freePlan = completePlans.find(p => p.isFreePlan);
      if (freePlan) {
        names.add(freePlan.productName);
      }
    }
    return names;
  }, [loggedIn, completePlans, activePlanName]);

  const currentProductNames = useMemo(() => {
    const names = new Set<string>();
    const visibleProductNames = new Set(
      completePlans.map(plan => plan.productName)
    );
    const configuredCurrentPlan =
      currentPlanName ?? billingPlanName ?? activePlanName;
    if (
      configuredCurrentPlan &&
      visibleProductNames.has(configuredCurrentPlan)
    ) {
      names.add(configuredCurrentPlan);
    }
    if (configuredCurrentPlan) {
      return names;
    }
    if (loggedIn && names.size === 0) {
      const freePlan = completePlans.find(p => p.isFreePlan);
      if (freePlan) {
        names.add(freePlan.productName);
      }
    }
    return names;
  }, [
    loggedIn,
    completePlans,
    activePlanName,
    billingPlanName,
    currentPlanName,
  ]);

  const overrideProductNames = useMemo(() => {
    const names = new Set<string>();
    if (!overridePlanName) {
      return names;
    }
    if (completePlans.some(plan => plan.productName === overridePlanName)) {
      names.add(overridePlanName);
    }
    return names;
  }, [completePlans, overridePlanName]);

  const executeCheckout = useCallback(
    async (priceId: string, planName?: string) => {
      setLoadingPriceId(priceId);
      setError(null);

      try {
        // recurring is NOT sent — the webhook derives it from Stripe subscription
        // items[0].price.recurring.interval (single source of truth).
        const successUrl = planName
          ? `${window.location.origin}/upgrade-success?plan=${encodeURIComponent(planName)}`
          : `${window.location.origin}/upgrade-success`;
        const result = await graphqlService.gql({
          query: createCheckoutSessionMutation,
          variables: {
            input: {
              successCallbackLink: successUrl,
              stripePriceId: priceId,
              ...(planName ? { plan: planName } : {}),
              ...(checkoutCoupon ? { coupon: checkoutCoupon } : {}),
              withdrawalConsent: true,
            },
          },
        });

        const checkoutUrl = (result as { createCheckoutSession: string })
          .createCheckoutSession;
        if (checkoutUrl) {
          window.location.href = checkoutUrl;
        } else {
          setError(t['com.streamlain.payment.pricing.checkout-failed']());
        }
      } catch (err: unknown) {
        setError(
          err instanceof Error
            ? err.message
            : t['com.streamlain.payment.pricing.checkout-failed']()
        );
      } finally {
        setLoadingPriceId(null);
      }
    },
    [checkoutCoupon, graphqlService, t]
  );

  // sl-r9ix.3.2: Execute trial checkout (no consent dialog)
  const executeTrialCheckout = useCallback(async () => {
    // Guard: plans must be loaded before trial checkout
    if (completePlans.length === 0) return;

    setLoadingPriceId('trial');
    setError(null);
    try {
      // Find Pro monthly price for the trial
      const proPlan = completePlans.find(
        (p: PricingPlan) => p.productName === 'Pro'
      );
      const proPrice = proPlan?.prices.find(
        (pr: PricingPlan['prices'][number]) => pr.interval === 'monthly'
      );
      if (!proPrice) {
        setError(t['com.streamlain.payment.pricing.checkout-failed']());
        return;
      }
      // Redirect to /upgrade-success after checkout (same as paid flow)
      const successUrl = `${window.location.origin}/upgrade-success?plan=Pro`;
      const result = await graphqlService.gql({
        query: createCheckoutSessionMutation,
        variables: {
          input: {
            successCallbackLink: successUrl,
            stripePriceId: proPrice.priceId,
            plan: 'Pro',
            recurring: SubscriptionRecurring.Monthly,
            withdrawalConsent: false,
            isTrial: true,
          },
        },
      });
      const checkoutUrl = (result as { createCheckoutSession: string })
        .createCheckoutSession;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      }
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : t['com.streamlain.payment.pricing.checkout-failed']()
      );
    } finally {
      setLoadingPriceId(null);
    }
  }, [graphqlService, completePlans, t]);

  // §312j BGB: Paid plans open confirmation dialog; free/contact plans act directly
  // sl-3uon: Cross-interval downgrade (Yearly→Monthly on different plan) uses deferred schedule
  const handleSubscribe = useCallback(
    (priceId: string) => {
      if (onSubscribeOverride) {
        onSubscribeOverride(priceId);
        return;
      }
      if (!loggedIn) {
        setError(t['com.streamlain.payment.pricing.login-required']());
        return;
      }
      // Find the plan for this priceId to check if it needs consent
      const plan = completePlans.find((p: PricingPlan) =>
        p.prices.some(
          (pr: PricingPlan['prices'][number]) => pr.priceId === priceId
        )
      );

      // sl-3uon: Detect cross-interval downgrade (Yearly subscription + Monthly toggle + different plan)
      const isCrossIntervalDowngrade =
        currentRecurring === 'Yearly' &&
        billingInterval === 'monthly' &&
        plan &&
        !plan.isFreePlan &&
        plan.productName !== (billingPlanName ?? activePlanName);

      if (isCrossIntervalDowngrade && plan) {
        const monthlyPrice = plan.prices.find(p => p.interval === 'monthly');
        if (monthlyPrice) {
          setPendingDeferredSwitch({
            targetPlan: plan.productName,
            targetRecurring: 'Monthly',
            monthlyPrice: {
              amount: monthlyPrice.amount,
              currency: monthlyPrice.currency,
            },
          });
          return;
        }
      }

      // sl-gegg.2.2: Paid user switching to a different plan → route via switchPlanInterval.
      // This avoids creating a duplicate Stripe subscription through checkout.
      const isPaidPlanSwitch =
        billingPlanName &&
        billingPlanName !== 'Free' &&
        !isTrialing &&
        plan &&
        !plan.isFreePlan &&
        plan.productName !== billingPlanName;

      if (isPaidPlanSwitch && plan) {
        const targetInterval =
          billingInterval === 'yearly' ? 'yearly' : 'monthly';
        const targetPrice = plan.prices.find(
          (p: PricingPlan['prices'][number]) => p.interval === targetInterval
        );
        if (targetPrice) {
          setPendingPlanSwitch({
            targetPlan: plan.productName,
            targetRecurring:
              billingInterval === 'yearly' ? 'Yearly' : 'Monthly',
            price: {
              amount: targetPrice.amount,
              currency: targetPrice.currency,
              interval: targetPrice.interval,
            },
          });
          return;
        }
      }

      if (plan && !plan.isFreePlan && !isProviderlessCta(plan.ctaType)) {
        setPendingPriceId(priceId);
        setPendingPlanName(plan.productName);
      } else {
        void executeCheckout(priceId, plan?.productName);
      }
    },
    [
      onSubscribeOverride,
      loggedIn,
      completePlans,
      executeCheckout,
      t,
      currentRecurring,
      billingInterval,
      activePlanName,
      billingPlanName,
      isTrialing,
    ]
  );

  const handleConfirmPurchase = useCallback(() => {
    if (pendingPriceId) {
      const planName = pendingPlanName;
      setPendingPriceId(null);
      setPendingPlanName(null);
      void executeCheckout(pendingPriceId, planName ?? undefined);
    }
  }, [pendingPriceId, pendingPlanName, executeCheckout]);

  // sl-dl66.3.1: Interval switch state
  const [isSwitchingInterval, setIsSwitchingInterval] = useState(false);
  // sl-dl66.3.2: Pending interval switch (requires consent dialog for Monthly→Yearly)
  const [pendingSwitchRecurring, setPendingSwitchRecurring] = useState<
    'Monthly' | 'Yearly' | null
  >(null);

  // sl-3uon: Deferred downgrade confirmation dialog state
  // Covers both same-plan (Pro Yearly→Pro Monthly) and cross-plan (Pro Yearly→Team Monthly)
  const [pendingDeferredSwitch, setPendingDeferredSwitch] = useState<{
    targetPlan: string;
    targetRecurring: 'Monthly';
    monthlyPrice: { amount: number; currency: string };
  } | null>(null);

  // sl-gegg.2.2: Cross-plan switch for paid users (routed via switchPlanInterval)
  const [pendingPlanSwitch, setPendingPlanSwitch] = useState<{
    targetPlan: string;
    targetRecurring: 'Monthly' | 'Yearly';
    price: { amount: number; currency: string; interval: string };
  } | null>(null);

  const executeSwitchInterval = useCallback(
    async (
      newRecurring: 'Monthly' | 'Yearly',
      withConsent: boolean,
      targetPlan?: string
    ) => {
      setIsSwitchingInterval(true);
      setError(null);
      try {
        const plan = billingPlanName ?? activePlanName ?? 'Pro';
        await graphqlService.gql({
          query: switchPlanIntervalMutation,
          variables: {
            plan,
            recurring: newRecurring as SubscriptionRecurring,
            targetPlan: targetPlan || undefined,
            withdrawalConsent: withConsent || undefined,
          },
        });

        // sl-gegg.2.2: Cross-plan switches are always immediate (reload needed).
        // Same-plan Yearly→Monthly is deferred (notification, no reload).
        const isSamePlanDowngrade = !targetPlan && newRecurring === 'Monthly';
        if (isSamePlanDowngrade) {
          // sl-3uon: Downgrade is deferred via Subscription Schedule —
          // show notification instead of reloading. No page reload needed
          // because the subscription stays on the current interval until
          // the schedule completes at period end.
          notify.success({
            title:
              t['com.streamlain.payment.pricing.switch-interval-scheduled'](),
          });
        } else {
          // Immediate change (upgrade or cross-plan) — reload to reflect new state
          window.location.reload();
        }
      } catch (err: unknown) {
        setError(
          err instanceof Error
            ? err.message
            : t['com.streamlain.payment.pricing.switch-interval-failed']()
        );
      } finally {
        setIsSwitchingInterval(false);
      }
    },
    [graphqlService, activePlanName, billingPlanName, t]
  );

  const handleSwitchInterval = useCallback(
    (newRecurring: 'Monthly' | 'Yearly') => {
      if (newRecurring === 'Yearly') {
        // Monthly→Yearly: requires §312j BGB consent (sl-dl66.3.2)
        setPendingSwitchRecurring('Yearly');
      } else {
        // sl-3uon: Yearly→Monthly: show confirmation dialog with effective date
        const currentPlan = completePlans.find(
          p => p.productName === (billingPlanName ?? activePlanName)
        );
        const monthlyPrice = currentPlan?.prices.find(
          p => p.interval === 'monthly'
        );
        if (currentPlan && monthlyPrice) {
          setPendingDeferredSwitch({
            targetPlan: currentPlan.productName,
            targetRecurring: 'Monthly',
            monthlyPrice: {
              amount: monthlyPrice.amount,
              currency: monthlyPrice.currency,
            },
          });
        }
      }
    },
    [completePlans, activePlanName, billingPlanName]
  );

  const handleConfirmSwitch = useCallback(() => {
    if (pendingSwitchRecurring) {
      setPendingSwitchRecurring(null);
      void executeSwitchInterval(pendingSwitchRecurring, true);
    }
  }, [pendingSwitchRecurring, executeSwitchInterval]);

  // sl-gegg.2.2: Confirm cross-plan switch for paid users
  const handleConfirmPlanSwitch = useCallback(() => {
    if (pendingPlanSwitch) {
      const { targetPlan, targetRecurring } = pendingPlanSwitch;
      setPendingPlanSwitch(null);
      void executeSwitchInterval(targetRecurring, true, targetPlan);
    }
  }, [pendingPlanSwitch, executeSwitchInterval]);

  // sl-3uon: Confirm deferred downgrade (same-plan or cross-plan)
  const handleConfirmDeferredSwitch = useCallback(() => {
    if (pendingDeferredSwitch) {
      const { targetPlan, targetRecurring } = pendingDeferredSwitch;
      const isCrossPlan = targetPlan !== (billingPlanName ?? activePlanName);
      setPendingDeferredSwitch(null);
      void executeSwitchInterval(
        targetRecurring,
        false,
        isCrossPlan ? targetPlan : undefined
      );
    }
  }, [
    pendingDeferredSwitch,
    executeSwitchInterval,
    activePlanName,
    billingPlanName,
  ]);

  useEffect(() => {
    if (!autoCheckout || !loggedIn || completePlans.length === 0) {
      return;
    }

    const targetInterval = recurringToBillingInterval(autoCheckout.recurring);
    if (billingInterval !== targetInterval) {
      setBillingInterval(targetInterval);
      return;
    }

    const targetPlan = completePlans.find(
      plan => plan.productName.toLowerCase() === autoCheckout.plan.toLowerCase()
    );
    const targetPrice = targetPlan?.prices.find(
      price => price.interval === targetInterval
    );

    if (
      !targetPlan ||
      !targetPrice ||
      targetPlan.isFreePlan ||
      isProviderlessCta(targetPlan.ctaType)
    ) {
      return;
    }

    const key = [
      targetPlan.productName,
      targetInterval,
      targetPrice.priceId,
      autoCheckout.coupon ?? checkoutCoupon ?? '',
    ].join(':');
    if (autoCheckoutHandledKeyRef.current === key) {
      return;
    }
    autoCheckoutHandledKeyRef.current = key;

    const targetCard = Array.from(
      document.querySelectorAll<HTMLElement>('[data-pricing-plan-name]')
    ).find(card => card.dataset.pricingPlanName === targetPlan.productName);
    targetCard?.scrollIntoView({ block: 'center', inline: 'nearest' });

    const currentInterval =
      currentRecurring === 'Yearly'
        ? 'yearly'
        : currentRecurring === 'Monthly'
          ? 'monthly'
          : null;
    const isCurrentPaidPlan =
      currentProductNames.has(targetPlan.productName) && !isTrialing;

    const frame = requestAnimationFrame(() => {
      if (isCurrentPaidPlan && currentInterval === targetInterval) {
        onAutoCheckoutHandled?.();
        return;
      }

      if (isCurrentPaidPlan && currentInterval) {
        handleSwitchInterval(
          autoCheckout.recurring === SubscriptionRecurring.Yearly
            ? 'Yearly'
            : 'Monthly'
        );
        onAutoCheckoutHandled?.();
        return;
      }

      handleSubscribe(targetPrice.priceId);
      onAutoCheckoutHandled?.();
    });

    return () => cancelAnimationFrame(frame);
  }, [
    autoCheckout,
    billingInterval,
    checkoutCoupon,
    completePlans,
    currentProductNames,
    currentRecurring,
    handleSubscribe,
    handleSwitchInterval,
    isTrialing,
    loggedIn,
    onAutoCheckoutHandled,
  ]);

  if (plans === null && !error) {
    return <PricingCardsSkeleton />;
  }

  if (error) {
    return (
      <div className={styles.pricingEmptyState}>
        <p style={{ color: 'var(--streamlain-error-color, #dc2626)' }}>
          {error}
        </p>
      </div>
    );
  }

  if (completePlans.length === 0) {
    return (
      <div className={styles.pricingEmptyState}>
        <p>{t['com.streamlain.payment.pricing.no-plans']()}</p>
      </div>
    );
  }

  // sl-xaz5.6: hasPaidPlans based on non-free plans with actual prices
  const hasPaidPlans = completePlans.some(
    p => !p.isFreePlan && p.prices.length > 0
  );

  return (
    <div className={styles.pricingRoot}>
      {hasPaidPlans && (
        <BillingToggle
          interval={billingInterval}
          onChange={setBillingInterval}
        />
      )}

      <div className={styles.pricingCardsGrid}>
        {completePlans.map(plan => {
          const isCurrentPlan = currentProductNames.has(plan.productName);
          const isAccessPlan = activeProductNames.has(plan.productName);
          const isOverridePlan = overrideProductNames.has(plan.productName);
          // Trialing subscriptions are still the current billing row and must be
          // visible so the user can identify the plan they may need to manage.
          // The CTA stays actionable below.
          const effectiveCurrent = isCurrentPlan;
          const currentActionable = effectiveCurrent && isTrialing;
          return (
            <PlanCard
              key={plan.productName}
              plan={plan}
              billingInterval={billingInterval}
              onSubscribe={handleSubscribe}
              isLoading={loadingPriceId !== null}
              isCurrent={effectiveCurrent}
              isOverride={isOverridePlan}
              currentRecurring={
                effectiveCurrent && !currentActionable
                  ? currentRecurring
                  : undefined
              }
              onSwitchInterval={
                effectiveCurrent && !currentActionable
                  ? handleSwitchInterval
                  : undefined
              }
              isSwitching={isSwitchingInterval}
              isCurrentActionable={currentActionable}
              isTrialEligible={
                !isAccessPlan && plan.productName === 'Pro' && trialEligible
              }
              onStartTrial={() => void executeTrialCheckout()}
            />
          );
        })}
      </div>

      {/* §312j/§356 BGB: Per-transaction consent for new subscription */}
      {(() => {
        const pendingPlan = pendingPriceId
          ? completePlans.find((p: PricingPlan) =>
              p.prices.some(
                (pr: PricingPlan['prices'][number]) =>
                  pr.priceId === pendingPriceId
              )
            )
          : null;
        const pendingPrice = pendingPlan?.prices.find(
          (pr: PricingPlan['prices'][number]) => pr.priceId === pendingPriceId
        );
        const locale = lang === 'en' ? 'en-US' : 'de-DE';
        const orderSummary =
          pendingPlan && pendingPrice
            ? buildSubscriptionOrderSummary({
                amountCents: pendingPrice.amount,
                currency: pendingPrice.currency,
                interval:
                  pendingPrice.interval === 'yearly' ? 'yearly' : 'monthly',
                language: lang,
                productName: pendingPlan.productName,
              })
            : undefined;
        return (
          <PurchaseConfirmDialog
            open={pendingPriceId !== null}
            onOpenChange={open => {
              if (!open) setPendingPriceId(null);
            }}
            productName={pendingPlan?.productName ?? ''}
            priceLabel={
              pendingPrice
                ? `${formatPrice(pendingPrice.amount, pendingPrice.currency, locale)}/${pendingPrice.interval === 'yearly' ? t['com.streamlain.payment.pricing.per-year']() : t['com.streamlain.payment.pricing.per-month']()}`
                : ''
            }
            orderSummary={orderSummary}
            isSubscription={true}
            isLoading={loadingPriceId !== null}
            onConfirm={handleConfirmPurchase}
          />
        );
      })()}

      {/* sl-fwcn.5.2: §312j BGB consent for Monthly→Yearly — shows total cost incl. addons */}
      {(() => {
        const currentPlan = completePlans.find(p =>
          activeProductNames.has(p.productName)
        );
        const yearlyPrice = currentPlan?.prices.find(
          p => p.interval === 'yearly'
        );
        const locale = lang === 'en' ? 'en-US' : 'de-DE';
        const perYear = t['com.streamlain.payment.pricing.per-year']();

        // sl-fwcn.5.2: Build total cost breakdown for §312j compliance
        const planAmount = yearlyPrice?.amount ?? 0;
        const addonTotal = (activeAddonCosts ?? []).reduce(
          (sum, a) => sum + a.amountCents,
          0
        );
        const grandTotal = planAmount + addonTotal;
        const hasAddons = (activeAddonCosts ?? []).length > 0 && addonTotal > 0;

        const priceLabel = hasAddons
          ? [
              `${currentPlan?.productName ?? ''}: ${formatPrice(planAmount, yearlyPrice?.currency ?? 'eur', locale)}/${perYear}`,
              ...(activeAddonCosts ?? []).map(
                a =>
                  `${a.label}: ${formatPrice(a.amountCents, yearlyPrice?.currency ?? 'eur', locale)}/${perYear}`
              ),
              `─────────`,
              `${t['com.streamlain.payment.pricing.total'] ? t['com.streamlain.payment.pricing.total']() : 'Total'}: ${formatPrice(grandTotal, yearlyPrice?.currency ?? 'eur', locale)}/${perYear}`,
            ].join('\n')
          : yearlyPrice
            ? `${formatPrice(yearlyPrice.amount, yearlyPrice.currency, locale)}/${perYear}`
            : '';
        const orderSummary =
          currentPlan && yearlyPrice
            ? {
                ...buildSubscriptionOrderSummary({
                  amountCents: grandTotal,
                  currency: yearlyPrice.currency,
                  interval: 'yearly' as const,
                  language: lang,
                  productName: currentPlan.productName,
                }),
                items: [
                  ...(hasAddons
                    ? [
                        {
                          label:
                            lang === 'en' ? 'Included items' : 'Bestandteile',
                          value: [
                            `${currentPlan.productName}: ${formatPrice(planAmount, yearlyPrice.currency, locale)}/${perYear}`,
                            ...(activeAddonCosts ?? []).map(
                              a =>
                                `${a.label}: ${formatPrice(a.amountCents, yearlyPrice.currency, locale)}/${perYear}`
                            ),
                          ].join('\n'),
                        },
                      ]
                    : []),
                  ...buildSubscriptionOrderSummary({
                    amountCents: grandTotal,
                    currency: yearlyPrice.currency,
                    interval: 'yearly',
                    language: lang,
                    productName: currentPlan.productName,
                  }).items,
                ],
              }
            : undefined;

        return (
          <PurchaseConfirmDialog
            open={pendingSwitchRecurring !== null}
            onOpenChange={open => {
              if (!open) setPendingSwitchRecurring(null);
            }}
            productName={currentPlan?.productName ?? ''}
            priceLabel={priceLabel}
            orderSummary={orderSummary}
            isSubscription={true}
            isLoading={isSwitchingInterval}
            onConfirm={handleConfirmSwitch}
          />
        );
      })()}

      {/* sl-gegg.2.2: §312j BGB consent for cross-plan switch (paid user → different plan) */}
      {pendingPlanSwitch &&
        (() => {
          const locale = lang === 'en' ? 'en-US' : 'de-DE';
          const perInterval =
            pendingPlanSwitch.price.interval === 'yearly'
              ? t['com.streamlain.payment.pricing.per-year']()
              : t['com.streamlain.payment.pricing.per-month']();
          const orderSummary = buildSubscriptionOrderSummary({
            amountCents: pendingPlanSwitch.price.amount,
            currency: pendingPlanSwitch.price.currency,
            interval:
              pendingPlanSwitch.price.interval === 'yearly'
                ? 'yearly'
                : 'monthly',
            language: lang,
            productName: pendingPlanSwitch.targetPlan,
          });
          return (
            <PurchaseConfirmDialog
              open={true}
              onOpenChange={open => {
                if (!open) setPendingPlanSwitch(null);
              }}
              productName={pendingPlanSwitch.targetPlan}
              priceLabel={`${formatPrice(pendingPlanSwitch.price.amount, pendingPlanSwitch.price.currency, locale)}/${perInterval}`}
              orderSummary={orderSummary}
              isSubscription={true}
              isLoading={isSwitchingInterval}
              onConfirm={handleConfirmPlanSwitch}
            />
          );
        })()}

      {/* sl-3uon: Deferred downgrade confirmation dialog (Yearly→Monthly) */}
      {pendingDeferredSwitch && (
        <DeferredDowngradeDialog
          open={true}
          onOpenChange={open => {
            if (!open) setPendingDeferredSwitch(null);
          }}
          targetPlan={pendingDeferredSwitch.targetPlan}
          monthlyPrice={pendingDeferredSwitch.monthlyPrice}
          effectiveDate={subscriptionEnd ?? undefined}
          isLoading={isSwitchingInterval}
          onConfirm={handleConfirmDeferredSwitch}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// sl-3uon: Deferred Downgrade Confirmation Dialog
// ---------------------------------------------------------------------------

function DeferredDowngradeDialog({
  open,
  onOpenChange,
  targetPlan,
  monthlyPrice,
  effectiveDate,
  isLoading,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetPlan: string;
  monthlyPrice: { amount: number; currency: string };
  effectiveDate?: string;
  isLoading: boolean;
  onConfirm: () => void;
}) {
  const t = useI18n();
  const locale = t.language?.startsWith('en') ? 'en-US' : 'de-DE';

  const formattedDate = effectiveDate
    ? new Date(effectiveDate).toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '—';

  const formattedPrice = formatPrice(
    monthlyPrice.amount,
    monthlyPrice.currency,
    locale
  );

  if (!open) return null;

  return (
    <div
      className={styles.deferredDowngradeOverlay}
      onClick={() => onOpenChange(false)}
    >
      <div
        className={styles.deferredDowngradeDialog}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t[
          'com.streamlain.payment.pricing.deferred-downgrade-title'
        ]()}
      >
        <h3 className={styles.deferredDowngradeTitle}>
          {t['com.streamlain.payment.pricing.deferred-downgrade-title']()}
        </h3>
        <p className={styles.deferredDowngradeDescription}>
          {t['com.streamlain.payment.pricing.deferred-downgrade-description']({
            plan: targetPlan,
            date: formattedDate,
            price: formattedPrice,
          })}
        </p>
        <div className={styles.deferredDowngradeActions}>
          <button
            className={styles.deferredDowngradeCancelBtn}
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            {t['com.streamlain.payment.pricing.deferred-downgrade-cancel']()}
          </button>
          <button
            className={styles.deferredDowngradeConfirmBtn}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading
              ? t['com.streamlain.payment.pricing.loading']()
              : t[
                  'com.streamlain.payment.pricing.deferred-downgrade-confirm'
                ]()}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skeleton (loading state)
// ---------------------------------------------------------------------------

function PricingCardsSkeleton() {
  return (
    <div className={styles.pricingRoot}>
      <div className={styles.pricingCardsGrid}>
        {[1, 2, 3].map(i => (
          <div
            key={i}
            className={styles.pricingCard}
            style={{ minHeight: 300, opacity: 0.5 }}
          />
        ))}
      </div>
    </div>
  );
}
